<template>
  <div>
    <a-card :loading="loading" class="release-info">
      <div v-if="!loading">
        <a-page-header :title="releaseData.release_title" sub-title="发布评审详情">
          <template slot="extra">
            <a-button v-tc="{ name: '编辑发布评审' }" type="link" size="small" icon="edit" :disabled="modifyPermission" @click="onEdit"> 编辑 </a-button>
          </template>
          <a-descriptions v-if="!loading" size="small">
            <a-descriptions-item label="类型"> {{ releaseData.release_type.label }} </a-descriptions-item>
            <a-descriptions-item label="环境"> {{ releaseData.release_env.label }} </a-descriptions-item>
            <a-descriptions-item label="状态">
              <a-badge :status="releaseStatus[releaseData.status].status" :text="releaseStatus[releaseData.status].label" />
            </a-descriptions-item>
            <a-descriptions-item label="流程">
              <a class="custom-link" :href="workflowLink(releaseData)"> {{ releaseData.workflow_title }} </a>
            </a-descriptions-item>
            <a-descriptions-item label="创建人"> <user-profiles :value="[releaseData.creator.user_id]" avatar /> </a-descriptions-item>
            <a-descriptions-item label="创建时间"> {{ releaseData.created_time }} </a-descriptions-item>
            <a-descriptions-item label="提交时间"> {{ releaseData.submission_time }} </a-descriptions-item>
            <a-descriptions-item label="部署时间"> {{ releaseData.deployed_time }} </a-descriptions-item>
            <a-descriptions-item label="完成时间"> {{ releaseData.completed_time }} </a-descriptions-item>
            <a-descriptions-item v-if="releaseData.tags.length > 0" label="原因分类" :span="3">
              <a-tag v-for="tag in releaseData.tags" :key="tag.id" :color="tag.color">{{ tag.name }}</a-tag>
            </a-descriptions-item>
          </a-descriptions>
          <a-descriptions v-if="!loading" size="small" class="release-comment">
            <a-descriptions-item v-if="releaseData.tags.length > 0" label="原因备注"> <vditor-preview :content="releaseData.comment" /> </a-descriptions-item>
          </a-descriptions>
        </a-page-header>
        <a-tabs default-active-key="0">
          <a-tab-pane key="0" tab="发布内容">
            <vditor-preview :content="releaseData.content" />
          </a-tab-pane>
          <a-tab-pane v-if="releaseData.story_list.length > 0" key="1" tab="关联需求">
            <issue-list :data-source="releaseData.story_list || []" />
          </a-tab-pane>
          <a-tab-pane v-if="releaseData.task_list.length > 0" key="2" tab="关联任务">
            <task-list :data-source="releaseData.task_list || []" />
          </a-tab-pane>
          <a-tab-pane v-if="releaseData.bug_list.length > 0 || releaseData.problem_list.length > 0 || (releaseData.reject_bug_list && releaseData.reject_bug_list.length > 0)" key="3" tab="关联缺陷/问题">
            <a-collapse :active-key="['1', '2', '3']" :bordered="false">
              <template #expandIcon="props">
                <a-icon type="caret-right" :rotate="props.isActive ? 90 : 0" />
              </template>
              <a-collapse-panel v-if="releaseData.bug_list.length > 0" key="1" class="issues-collapse-panel">
                <a-badge slot="tab" :count="releaseData.bug_list.length"> <span class="head-example">提审缺陷</span></a-badge>
                <issue-basic-list :issue-list="releaseData.bug_list" />
              </a-collapse-panel>
              <a-collapse-panel v-if="releaseData.reject_bug_list.length > 0" key="2" class="issues-collapse-panel">
                <a-badge slot="header" :count="releaseData.reject_bug_list.length"> <span class="head-example">驳回/退回缺陷</span></a-badge>
                <issue-basic-list :issue-list="releaseData.reject_bug_list" />
              </a-collapse-panel>
              <a-collapse-panel v-if="releaseData.problem_list.length > 0" key="3" class="issues-collapse-panel">
                <a-badge slot="header" :count="releaseData.problem_list.length"> <span class="head-example">线上问题</span></a-badge>
                <problem-list :data-source="releaseData.problem_list" />
              </a-collapse-panel>
            </a-collapse>
          </a-tab-pane>
          <a-tab-pane v-if="releaseData.description" key="4" tab="备注">
            <span class="release-content">{{ releaseData.description }}</span>
          </a-tab-pane>
          <a-tab-pane v-if="releaseData.apollo_address" key="5" tab="配置变更">
            <vditor-preview :content="releaseData.apollo_address" />
          </a-tab-pane>
          <a-tab-pane v-if="releaseData.sql_address" key="6" tab="数据变更">
            <vditor-preview :content="releaseData.sql_address" />
          </a-tab-pane>
          <a-tab-pane v-if="releaseData.service_list.length > 0" key="7" tab="服务列表">
            <service-list :data-source="releaseData.service_list" :deploy-permission="releaseData.deploy_permission" @callback="init" />
          </a-tab-pane>
        </a-tabs>
      </div>
      <div v-else />
    </a-card>
  </div>
</template>
<script>
import VditorPreview from '@/components/VditorEditor/preview';
import { workflowReleaseDetail } from '@/api/project/workflow';
import { releaseStatus, serviceTypes } from '@/utils/dictionary';
import TaskList from '@/pages/project/workflow/components/TaskList';
import IssueList from '@/pages/project/workflow/components/IssueList';
import ProblemList from '@/pages/project/workflow/components/ProblemList';
import IssueBasicList from '@/pages/project/issue/components/IssueBasicList';
import ServiceList from '@/pages/project/workflow/release/components/ServiceList';

export default {
  components: { IssueList, ServiceList, VditorPreview, IssueBasicList, TaskList, ProblemList },
  data() {
    return {
      loading: true,
      serviceTypes,
      releaseStatus,
      projectId: parseInt(this.$route.params.project_id) || null,
      releaseId: parseInt(this.$route.query.releaseId) || null,
      releaseData: {
        bug_list: [],
        deploying: false,
        service_list: [],
        problem_list: [],
        reject_bug_list: [],
        deploy_permission: false
      }
    };
  },
  computed: {
    workflowLink() {
      return record => {
        const link = this.$router.resolve({
          name: 'page:project:workflow:detail',
          query: { workflowId: record.workflow_id }
        });
        return link.href;
      };
    },
    modifyPermission() {
      return this.releaseData.modify_permission.status === false || ![0, 1].includes(this.releaseData.workflow_status);
    }
  },
  created() {
    if (this.releaseId) {
      this.init();
    } else {
      this.$router.push({ name: 'page:project:workflow:release:list' });
    }
  },
  destroyed() {
    clearInterval(this.timer);
  },
  methods: {
    // 获取工作流发布评审详情
    getWorkflowReleaseDetail() {
      workflowReleaseDetail({ project_id: this.projectId, release_id: this.releaseId })
        .then(res => {
          if (res.data.deploying !== true) {
            clearInterval(this.timer);
          }
          this.loading = false;
          this.releaseData = res.data;
        })
        .catch(() => {
          this.loading = false;
        });
    },

    init() {
      this.timer = setInterval(() => {
        this.getWorkflowReleaseDetail();
      }, 2000);
      this.getWorkflowReleaseDetail();
    },

    reload() {
      this.getWorkflowReleaseDetail();
    },

    onEdit() {
      this.$router.push({
        name: 'page:project:workflow:release:edit',
        query: { releaseId: this.releaseId }
      });
    }
  }
};
</script>
<style lang="less" scoped>
/deep/.release-info {
  margin-bottom: 16px;
  .ant-card-body {
    padding: 16px;
  }
  .ant-page-header {
    padding: 0;
  }
}

/deep/.release-content-card {
  .ant-card-body {
    padding: 8px 16px;
  }
}

.service-col {
  margin: 4px 0;
}
.service-name {
  display: block;
  font-weight: bold;
  // font-size: 16px;
  line-height: 32px;
}
.service-label {
  color: rgba(0, 0, 0, 0.85);
  margin-right: 4px;
}
.service-image {
  display: block;
  line-height: 28px;

  .service-image-value {
    cursor: pointer;
  }
}
.release-content {
  margin-left: 16px;
  margin-bottom: 8px;
  display: block;
  white-space: pre-wrap;
}

/deep/.release-comment {
  .ant-descriptions-item {
    span {
      float: left;
    }
  }
}
.head-example {
  display: inline-block;
  padding-right: 8px;
  // font-size: 16px;
}
/deep/.ant-badge-count {
  height: 16px;
  min-width: 16px;
  line-height: 16px;
  font-size: 8px;
  padding: 0 3px;
  background-color: #1890ff;
}

/deep/.ant-collapse-header {
  background: #f9f9f9;
}
.issues-collapse-panel {
  background: #fff;
  border-radius: 4px;
  margin-bottom: 24px;
  border: 0;
  overflow: hidden;
}
</style>
